import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import getEventsDayOverview from 'api/events/get-events-day-overview';

import type {
    EventAttributionListItem,
    EventDayOverview,
} from 'api/events/events.types';
import type {DayOverviewState} from 'pages/event-management/store/day-overview-slice';
import dayjs from 'utils/dayjs';

const CANCELED = 'ERR_CANCELED';

export interface GetDayOverviewDataParams {
    facilityIds: number[];
    date: string;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

interface GetDayOverviewDataThunkAPI {
    state: {
        events: {
            dayOverview: DayOverviewState;
        };
    };
    rejectValue: unknown;
}

/**
 * Formating function to sort events by their start date in ascending order.
 */
const sortEventsByStartDate = (
    events: EventAttributionListItem[]
): EventAttributionListItem[] => {
    const sortedEvents = [...events].sort((a, b) => {
        const startDateA = dayjs(a.eventStarts);
        const startDateB = dayjs(b.eventStarts);

        return startDateA.valueOf() - startDateB.valueOf();
    });

    return sortedEvents;
};

/**
 * A Redux thunk to get the events day overview data.
 *
 * Action Type: events/day-overview/getEventsDayOverviewData
 *
 * API Service: {@link getEventsDayOverview}
 */
export const getEventsDayOverviewData = createAsyncThunk<
    // Return type of the payload creator
    EventDayOverview,
    // First argument to the payload creator
    GetDayOverviewDataParams,
    // ThunkAPI type
    GetDayOverviewDataThunkAPI
>(
    'events/day-overview/getEventsDayOverviewData',
    async (
        {facilityIds, date, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const formatedDate = dayjs(date).format('YYYY-MM-DD');

            const response = await getEventsDayOverview(
                facilityIds,
                formatedDate,
                {
                    signal: abortSignal,
                }
            );

            const sortedEvents = sortEventsByStartDate(response.data.events);

            const formattedResponse = {
                counts: response.data.counts,
                events: sortedEvents,
            };

            return formattedResponse;
        } catch (error) {
            // If the error is due to the request being canceled, we don't want to log it
            if (error.code === CANCELED) {
                return;
            }

            console.error('getEventsDayOverviewData error:', {error});

            return rejectWithValue(error);
        }
    }
);

/**
 * An action to update the selected date for the day overview page.
 */
export const setSelectedDate = createAction(
    'events/day-overview/setSelectedDate',
    (date: string) => {
        return {
            payload: date,
        };
    }
);
